import type { ReactNode } from 'react';
import type { AdminStatsResponse, Decision } from '@kkp-fraud/shared';
import { ms, num } from '../format';
import { t } from '../i18n';
import { Link } from 'react-router-dom';
import { ActionChip } from './Chips';
import { NOT_A_RULE, ruleLink } from '../links';
import { usePrimarySetId } from '../hooks/usePrimarySetId';

/**
 * The `GET /admin/stats` figures. Extracted from DecisionsTab so the Dashboard can show the
 * same numbers from the same source — two screens rendering the same figure from two
 * different calculations is how a console starts lying.
 *
 * Laid out the mockup's way: one row of headline figures divided by vertical rules (not
 * four bordered tiles floating in a grid), and the decision split as a proportion list,
 * because "13 declines" only means something next to the total it came out of.
 */

export const DECISIONS: Decision[] = ['APPROVE', 'APPROVE_NOTIFY', 'STEP_UP', 'DECLINE'];

/** The bar colour repeats the chip's colour, so the two readings of the same row agree. */
const BAR_COLOUR: Record<Decision, string> = {
  APPROVE: 'var(--ok)',
  APPROVE_NOTIFY: 'var(--purple)',
  STEP_UP: 'var(--warn)',
  DECLINE: 'var(--bad)',
};

/**
 * The KPI row and the decision shares. `aside` (the Log's top rules) sits BESIDE the shares, so a
 * wide screen reads both at once instead of a long empty bar row.
 */
export function StatsTiles({ stats, aside }: { stats: AdminStatsResponse | null; aside?: { title: string; body: ReactNode } }) {
  const total = stats?.total ?? 0;
  return (
    <>
      <div className="grid g4">
        <div className="kpi">
          <div className="lbl">{t('stats.total.label')}</div>
          <div className="val">{num(total)}</div>
        </div>
        <div className="kpi">
          <div className="lbl">p95 / p99</div>
          <div className="val sm">{stats ? `${ms(stats.p95Ms)} / ${ms(stats.p99Ms)}` : '—'}</div>
        </div>
        <div className="kpi">
          <div className="lbl">p50</div>
          <div className="val sm">{stats ? ms(stats.p50Ms) : '—'}</div>
        </div>
        <div className="kpi">
          <div className="lbl">{t('flag.degraded.chip')}</div>
          <div className="val">{num(stats?.degraded ?? 0)}</div>
        </div>
      </div>

      <div className={aside ? 'stats-split' : undefined}>
      <div>
        {DECISIONS.map((d) => {
          const n = stats?.byDecision?.[d] ?? 0;
          const pct = total > 0 ? (n / total) * 100 : 0;
          return (
            <div className="barrow" key={d} data-testid={`tile-${d}`}>
              <div className="barlbl">
                <ActionChip action={d} />
              </div>
              <div className="bar">
                <i style={{ width: `${pct.toFixed(1)}%`, background: BAR_COLOUR[d] }} />
              </div>
              <div className="barval">
                {num(n)} <span className="dim">{pct.toFixed(1)}%</span>
              </div>
            </div>
          );
        })}
      </div>
      {aside && (
        <div className="stats-aside" data-testid="stats-aside">
          <h3>{aside.title}</h3>
          {aside.body}
        </div>
      )}
      </div>
    </>
  );
}

/** Top rules by hit count — also shared between Dashboard and the Log decisions feed. */
export function TopRules({ byRule }: { byRule: Record<string, number> }) {
  const primaryId = usePrimarySetId();
  const rows = Object.entries(byRule)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);
  const max = rows[0]?.[1] ?? 1;
  if (rows.length === 0) return <div className="empty small">{t('stats.topRules.empty')}</div>;
  return (
    <div>
      {rows.map(([id, count]) => (
        <div className="barrow" key={id}>
          <div className="barlbl mono">{NOT_A_RULE.has(id) ? id : <Link to={ruleLink(id, undefined, primaryId)} title={t('links.rule.title', { id })}>{id}</Link>}</div>
          <div className="bar">
            <i style={{ width: `${Math.max(2, (count / max) * 100)}%` }} />
          </div>
          <div className="barval">{num(count)}</div>
        </div>
      ))}
    </div>
  );
}
